import React, { useEffect, useState, useCallback } from "react";
import { gql, useQuery } from "urql";
import { useParams, useLocation, useSearchParams } from "wouter";
import { sortBy } from "lodash";
import * as Tabs from "@radix-ui/react-tabs";

import { Party, PartyType } from "../../types.generated";

import {
    CollateralView,
    CashAccountView,
    TransactionView,
    TradingManagerView,
    FxView,
    CashLadderView,
    ReportsView,
    CounterpartyView,
    FxRiskView,
    PortfolioManagerCommentTabs,
    DailyEodView,
    DashboardView,
    PreTradeView
} from "../../components/portfoliomanager";
import { PmSelectPrompt } from "../../components/portfoliomanager/PmSelectPrompt";
import { SelectDropdown } from "../../components/SelectDropdown";
import { ResponsiveTabsList } from "../../../../components/src/ResponsiveTabsList";

const getClients = gql`
    query {
        parties(filter: { typeIn: [Client], statusIn: [Confirmed] }) {
            _id
            name
            types
        }
    }
`;

const Checkbox = (props) => <input type="checkbox" {...props} />;

const FUNDS_OVERVIEW_LABEL = "Funds Overview";

type SelectClientsPropsType = {
    selectedClientId: string;
    setSelectedClientId: (id: string) => void;
};

const SelectClients = ({ selectedClientId, setSelectedClientId }: SelectClientsPropsType): React.ReactElement => {
    const [{ fetching, error, data }] = useQuery({ query: getClients });

    useEffect(() => {
        let title = "PM - All portfolios";
        if (selectedClientId && selectedClientId !== "all" && data && data.parties) {
            const client = data.parties.find((x) => x._id == selectedClientId);
            title = `PM - ${client?.name}`;
        }
        document.title = title;
    }, [selectedClientId, data]);

    if (fetching) return <div>Loading...</div>;
    if (error) return <div>`Error! ${error.message}`</div>;

    let clientOptions = [];
    data.parties.forEach((party: Partial<Party>) => {
        if (party.types.includes(PartyType.Fund) || party.types.includes(PartyType.Mandate)) {
            clientOptions.push({ key: party._id, text: party.name });
        }
    });

    clientOptions = sortBy(clientOptions, "text");
    const options = [{ key: "all", text: "All" }, ...clientOptions];

    return (
        <SelectDropdown
            key={selectedClientId}
            placeholder="All"
            className="form-input pm-client-select"
            defaultValue={selectedClientId}
            options={options}
            onChange={(e) => {
                setSelectedClientId(e.key);
            }}
        />
    );
};

const allTabs = {
    transactions: "transactions",
    positions: "positions",
    cashAccounts: "cashAccounts",
    collateral: "collateral",
    fx: "fx",
    cashLadder: "cashLadder",
    counterparties: "counterparties",
    fxRisk: "fxRisk",
    reports: "reports",
    comment: "comment",
    dashboard: "dashboard",
    preTrade: "preTrade"
};

const allClientsTabs = new Set(["dashboard", "fx", "dailyEod"]);

const tabTriggerClassName = "pm-tab-trigger";

const SelectTabPrompt = (): React.ReactElement => <PmSelectPrompt message="Select a tab" />;

export const PortfolioManager = (): React.ReactElement => {
    const { id, tab } = useParams<{ id: string; tab: string }>();
    const [queryArgs] = useSearchParams();
    const [activeTab, setActiveTab] = useState(tab ?? "");
    const [checked2, setChecked2] = useState(true);
    const [tradingView, setTradingView] = useState(false);
    const [lastClientId, setLastClientId] = useState<string | null>(null);

    const [, navigate] = useLocation();

    useEffect(() => {
        setActiveTab(tab ?? "");
    }, [tab]);

    useEffect(() => {
        const currentTab = tab || activeTab;
        if (currentTab && !allClientsTabs.has(currentTab) && id && id !== "all") {
            setLastClientId(id);
        }
    }, [id, tab, activeTab]);

    const handleCheckboxChange2 = (e) => {
        setChecked2(e.target.checked);
    };

    const handleTradingViewChange = (e) => {
        setTradingView(e.target.checked);
    };

    const switchTab = useCallback(
        (nextTab: string) => {
            setActiveTab(nextTab);
            if (!(nextTab in allTabs) && !allClientsTabs.has(nextTab)) {
                navigate("/pm/all/" + allTabs.transactions + (queryArgs.toString() ? `?${queryArgs.toString()}` : ""), {
                    replace: true
                });
                return;
            }
            let clientId = id;
            if (allClientsTabs.has(nextTab)) {
                if (id && id !== "all") {
                    setLastClientId(id);
                }
                clientId = "all";
            } else if (lastClientId) {
                clientId = lastClientId;
            } else if (!clientId) {
                clientId = "all";
            }
            const url = `/pm/${clientId}/${nextTab}${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`;
            navigate(url);
        },
        [id, lastClientId, navigate, queryArgs]
    );

    const handleClientChange = useCallback(
        (clientId: string) => {
            const query = queryArgs.toString() ? `?${queryArgs.toString()}` : "";

            setLastClientId(clientId);

            if (allClientsTabs.has(activeTab)) {
                return;
            }

            if (tab) {
                navigate(`/pm/${clientId}/${tab}${query}`);
            } else {
                navigate(`/pm/${clientId}${query}`);
            }
        },
        [activeTab, navigate, queryArgs, tab]
    );

    useEffect(() => {
        const currentTab = tab || activeTab;
        if (allClientsTabs.has(currentTab) && id && id !== "all") {
            setLastClientId(id);
            navigate(`/pm/all/${currentTab}${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`, { replace: true });
        }
    }, [id, tab, activeTab, navigate, queryArgs]);

    const onOverviewTab = allClientsTabs.has(activeTab);
    const clientZoneActive = !onOverviewTab;
    let effectiveClientId = id ?? "all";
    if (onOverviewTab) {
        effectiveClientId = lastClientId ?? "all";
    }

    const zoneClassName = (active: boolean): string => {
        let className = "pm-zone";
        if (active) {
            className = className + " pm-zone-active";
        } else {
            className = className + " pm-zone-inactive";
        }
        return className;
    };

    const showSelectTab = !tab;

    const trans_link = id === "all" || !id ? "/transactions" : "/transactions?qClientId=" + id;

    const overviewTabTriggers = (
        <>
            <Tabs.Trigger value="dashboard" className={tabTriggerClassName}>
                Dashboard
            </Tabs.Trigger>
            <Tabs.Trigger value="fx" className={tabTriggerClassName}>
                FX
            </Tabs.Trigger>
            <Tabs.Trigger value="dailyEod" className={tabTriggerClassName}>
                Daily Eod
            </Tabs.Trigger>
        </>
    );

    return (
        <div>
            <Tabs.Root value={activeTab} onValueChange={switchTab}>
                <div className="pm-toolbar">
                    <div className={zoneClassName(clientZoneActive) + " pm-client-zone w-full sm:w-4/12 min-w-0"}>
                        <label className="pm-field-label">Client</label>
                        <SelectClients selectedClientId={effectiveClientId} setSelectedClientId={handleClientChange} />
                    </div>
                    <div className={zoneClassName(onOverviewTab) + " pm-overview-zone pm-overview-section"}>
                        <span className="pm-field-label">{FUNDS_OVERVIEW_LABEL}</span>
                        <Tabs.List className="flex flex-wrap mb-0">{overviewTabTriggers}</Tabs.List>
                    </div>
                </div>

                {onOverviewTab ? (
                    <div className="pm-overview-mobile-banner md:hidden">
                        <span className="pm-field-label">{FUNDS_OVERVIEW_LABEL}</span>
                    </div>
                ) : null}

                <div className={zoneClassName(clientZoneActive) + " pm-client-tabs-zone"}>
                <ResponsiveTabsList
                    value={activeTab}
                    onValueChange={switchTab}
                    renderSecondaryRow={false}
                    secondaryLabel={FUNDS_OVERVIEW_LABEL}
                    secondaryChildren={overviewTabTriggers}
                >
                    <Tabs.Trigger
                        value="transactions"
                        className={tabTriggerClassName}
                    >
                        Transactions
                    </Tabs.Trigger>
                    <Tabs.Trigger
                        value="positions"
                        className={tabTriggerClassName}
                    >
                        Positions
                    </Tabs.Trigger>
                    <Tabs.Trigger
                        value="cashAccounts"
                        className={tabTriggerClassName}
                    >
                        Cash accounts
                    </Tabs.Trigger>
                    <Tabs.Trigger
                        value="collateral"
                        className={tabTriggerClassName}
                    >
                        Collateral
                    </Tabs.Trigger>
                    <Tabs.Trigger
                        value="cashLadder"
                        className={tabTriggerClassName}
                    >
                        Cash ladder
                    </Tabs.Trigger>
                    <Tabs.Trigger
                        value="counterparties"
                        className={tabTriggerClassName}
                    >
                        Counterparties
                    </Tabs.Trigger>
                    <Tabs.Trigger
                        value="fxRisk"
                        className={tabTriggerClassName}
                    >
                        FX risk
                    </Tabs.Trigger>
                    <Tabs.Trigger
                        value="reports"
                        className={tabTriggerClassName}
                    >
                        Reports
                    </Tabs.Trigger>
                    <Tabs.Trigger
                        value="comment"
                        className={tabTriggerClassName}
                    >
                        Comment
                    </Tabs.Trigger>
                    <Tabs.Trigger
                        value="preTrade"
                        className={tabTriggerClassName}
                    >
                        Pre trade
                    </Tabs.Trigger>
                </ResponsiveTabsList>
                </div>

                <div className="pm-content-panel">
                {showSelectTab ? (
                    <SelectTabPrompt />
                ) : (
                    <>
                <Tabs.Content value="transactions">
                    <div>
                        <div className="pm-info-banner">
                            Some transactions are not visible in this view, go to <a href={trans_link}>/transactions</a> for a complete
                            view.
                        </div>
                        <TransactionView />
                    </div>
                </Tabs.Content>
                <Tabs.Content value="positions">
                    <div>
                        <div className="mb-4">
                            <label>
                                <Checkbox checked={checked2} onChange={handleCheckboxChange2} />
                                <span>Look through</span>
                            </label>
                            <label className="ml-4">
                                <Checkbox checked={tradingView} onChange={handleTradingViewChange} />
                                <span>Trading view</span>
                            </label>
                        </div>
                        <TradingManagerView lookThrough={checked2} tradingView={tradingView} />
                    </div>
                </Tabs.Content>
                <Tabs.Content value="cashAccounts">
                    <CashAccountView />
                </Tabs.Content>
                <Tabs.Content value="collateral">
                    <CollateralView />
                </Tabs.Content>
                <Tabs.Content value="fx">
                    <FxView />
                </Tabs.Content>
                <Tabs.Content value="cashLadder">
                    <CashLadderView />
                </Tabs.Content>
                <Tabs.Content value="counterparties">
                    <CounterpartyView />
                </Tabs.Content>
                <Tabs.Content value="fxRisk">
                    <div>
                        <div className="mb-4">
                            <label>
                                <Checkbox checked={checked2} onChange={handleCheckboxChange2} />
                                <span>Look through</span>
                            </label>
                        </div>
                        <FxRiskView lookThrough={checked2} />
                    </div>
                </Tabs.Content>
                <Tabs.Content value="reports">
                    <div>
                        <div className="mb-4">
                            <label>
                                <Checkbox checked={checked2} onChange={handleCheckboxChange2} />
                                <span>Look through</span>
                            </label>
                        </div>
                        <ReportsView lookThrough={checked2} />
                    </div>
                </Tabs.Content>
                <Tabs.Content value="comment">
                    <PortfolioManagerCommentTabs />
                </Tabs.Content>
                <Tabs.Content value="dailyEod">
                    <div>
                        <DailyEodView />
                    </div>
                </Tabs.Content>
                <Tabs.Content value="dashboard">
                    <div>
                        <DashboardView />
                    </div>
                </Tabs.Content>
                <Tabs.Content value="preTrade">
                    <div>
                        <div className="mb-4">
                            <label>
                                <Checkbox checked={checked2} onChange={handleCheckboxChange2} />
                                <span>Look through</span>
                            </label>
                        </div>
                        <PreTradeView lookThrough={checked2} />
                    </div>
                </Tabs.Content>
                    </>
                )}
                </div>
            </Tabs.Root>
        </div>
    );
};
